import {Injectable} from '@angular/core';

import * as printJS from 'print-js';


declare const window: any;

/**
 * Window position configuration for QR code popup.
 */
export interface QrWindowPosition {
  width: number;
  height: number;
  x: number;
  y: number;
}

/**
 * Default window position for QR code popup.
 */
const DEFAULT_WINDOW_POSITION: QrWindowPosition = {
  width: 512,
  height: 512,
  x: 100,
  y: 100
};

/**
 * LocalStorage key for QR window position.
 */
const QR_WINDOW_POSITION_KEY = 'qrWindowPosition';

/**
 * Service for QR code operations including printing and window management.
 * Handles QR code display in new windows and print functionality.
 */
@Injectable()
export class QrCodeService {
  /**
   * Prints the QR code from a canvas element.
   *
   * @param canvasSelector CSS selector for the canvas element.
   */
  public print(canvasSelector: string): void {
    const canvas = this.getCanvas(canvasSelector);
    if (!canvas) {
      return;
    }

    const dataUrl = canvas.toDataURL();
    printJS({printable: dataUrl, type: 'image', base64: true});
  }

  /**
   * Opens the QR code in a new window.
   *
   * @param canvasSelector CSS selector for the canvas element.
   */
  public async openInNewWindow(canvasSelector: string): Promise<void> {
    // request screen details permission if available
    await this.requestScreenPermission();

    const canvas = this.getCanvas(canvasSelector);
    if (!canvas) {
      return;
    }

    const position = this.getStoredWindowPosition();
    const windowFeatures = this.buildWindowFeatures(position);
    const dataUrl = canvas.toDataURL('image/png');
    const htmlContent = this.buildQrWindowHtml(dataUrl);

    const w = window.open('', 'QR Code', windowFeatures);

    if (w) {
      w.document.write(htmlContent);
      w.document.close();
    }
  }

  /**
   * Gets the stored window position from localStorage.
   *
   * @returns The stored position or default values.
   */
  public getStoredWindowPosition(): QrWindowPosition {
    try {
      const rawPos = localStorage.getItem(QR_WINDOW_POSITION_KEY);
      if (rawPos) {
        const parsed = JSON.parse(rawPos);
        return {
          width: parsed.width || DEFAULT_WINDOW_POSITION.width,
          height: parsed.height || DEFAULT_WINDOW_POSITION.height,
          x: parsed.x || DEFAULT_WINDOW_POSITION.x,
          y: parsed.y || DEFAULT_WINDOW_POSITION.y
        };
      }
    } catch (e) {
      // ignore parse errors
    }

    return {...DEFAULT_WINDOW_POSITION};
  }

  /**
   * Saves window position to localStorage.
   *
   * @param position The position to save.
   */
  public saveWindowPosition(position: QrWindowPosition): void {
    try {
      localStorage.setItem(QR_WINDOW_POSITION_KEY, JSON.stringify(position));
    } catch (e) {
      console.error('Failed to save QR window position', e);
    }
  }

  /**
   * Gets a canvas element from a QR code container.
   *
   * @param selector CSS selector for the container.
   * @returns The canvas element or null.
   */
  private getCanvas(selector: string): HTMLCanvasElement | null {
    const element = document.querySelector(selector);
    if (!element) {
      return null;
    }

    // if the element itself is a canvas, return it
    if (element.tagName === 'CANVAS') {
      return element as HTMLCanvasElement;
    }

    // otherwise look for a canvas child
    return element.querySelector('canvas');
  }

  /**
   * Requests screen details permission if available.
   */
  private async requestScreenPermission(): Promise<void> {
    if (!('getScreenDetails' in window)) {
      return;
    }

    try {
      await navigator.permissions.query(<any>{name: 'window-management'});
      await window.getScreenDetails().catch(() => null);
    } catch (error) {
      // permission denied or not available
    }
  }

  /**
   * Builds window features string for window.open().
   *
   * @param position The window position.
   * @returns Window features string.
   */
  private buildWindowFeatures(position: QrWindowPosition): string {
    return `width=${position.width},height=${position.height},left=${position.x},top=${position.y},` +
           'menubar=no,toolbar=no,location=no,status=no,resizable=yes,scrollbars=no';
  }

  /**
   * Builds the HTML content for the QR code popup window.
   *
   * @param dataUrl The QR code image data URL.
   * @returns HTML string for the popup.
   */
  private buildQrWindowHtml(dataUrl: string): string {
    return `
    <html>
      <head>
        <title>QR Code</title>
        <style>
          body { margin: 0; padding: 0; overflow: hidden; background: #fff; }
          img { width: 100vw; height: 100vh; object-fit: contain; }
        </style>
      </head>
      <body>
        <img src="${dataUrl}" alt="QR Code" />
        <script>
          window.onunload = function() {
            if (window.opener && !window.opener.closed) {
              var data = {
                width: window.innerWidth,
                height: window.innerHeight,
                x: window.screenX,
                y: window.screenY
              };
              try {
                window.opener.localStorage.setItem('${QR_WINDOW_POSITION_KEY}', JSON.stringify(data));
              } catch(e) {
                console.error('Failed to save window position', e);
              }
            }
          };
        </script>
      </body>
    </html>`;
  }
}

